/* eslint-disable @typescript-eslint/naming-convention */
import { Airecommendation } from "@similarweb/icons";
import { EPrimaryItemType } from "@similarweb/ui-components";

import { isAvailable, isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { getUnlockBadge } from "app/components/SideBar/getUnlockBadge";
import { buyerJourney, onHookClick, onSubSectionClick } from "app/components/sidebar3/SideBarItems";
import type { ISubSectionItem } from "app/components/sidebar3/types";
import { BADGE_ITEMS, EGroupTypes, ESidebarPlacement, SecondarySideBarType3 } from "app/components/sidebar3/types";
import {
    adSearchIsOpen,
    isAdIntelligenceNavigationLocked,
    openAdIntelligenceHookModal,
} from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { EPrimarySideBar3IdType, EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { openHooksModalV5 } from "app/services/ModalService";
import { getHooksVersion } from "app/UtilitiesAndConstants/UtilityFunctions/hooksUtils";
import { ROUTES as advertiserActivityRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/advertiserActivity/advertiserActivityConstants";
import { ROUTES as publisherAnalysisRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisConstants";
import { ROUTES as AIResearchRoutes } from "single-spa/react-app/config/solutions25/genAIIntelligence/AIResearch/AIResearchConstants";
import { ROUTES as AITrafficRoutes } from "single-spa/react-app/config/solutions25/genAIIntelligence/AITraffic/AITrafficConstants";
import { ROUTES as recommendationsRoutes } from "single-spa/react-app/config/solutions25/genAIIntelligence/Recommendations/RecommendationsConstants";

import { handleAIBrandVisibilityClick } from "./listItemsOnClickHandlers";

const isHideSidebarLockedFeaturesTest = false; // TODO: remove this when cleanup lnx-87-hide-sidebar-locked-features test

// Use for each group in sidebar to filter out locked features for user (also newly added ones)
const filterOutLockedItems = <T extends ISubSectionItem>(items: T[]): T[] =>
    items.filter((item) => (isHideSidebarLockedFeaturesTest ? !item?.isLocked || item?.upsellBadgeComponent : true));

const CompetitiveResearchSubSection = (): ISubSectionItem[] => {
    const isItemLockedByPermission = isLocked(swSettings.components.DigitalSuiteCompetitveSection);
    const isItemHiddenByPermission = isHidden(swSettings.components.DigitalSuiteCompetitveSection);
    const onClickLockedCallback = (key?: string, version?: string) =>
        onHookClick(key ? key : "GetMoreResults", "digitalsuite_websiteanalysis_home", version);

    return filterOutLockedItems([
        {
            title: "primary.sidebar.website.analysis.title",
            icon: "sidebar3-website-analysis",
            state: EPrimarySideBar3StateType.WebsiteAnalysis_home,
            ParentTrackName: "Competitive Research",
            id: EPrimarySideBar3IdType.WebsiteAnalysis,
            isLocked: isItemLockedByPermission,
            onClickLocked: onClickLockedCallback,
            isHidden: isItemHiddenByPermission,
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.WebsiteAnalysis,
        },
        {
            title: "primary.sidebar.website.rankings.title",
            icon: "sidebar3-webmarkets",
            state: EPrimarySideBar3StateType.WebMarketAnalysis_home,
            ParentTrackName: "Market Analysis",
            id: EPrimarySideBar3IdType.WebMarketAnalysis,
            isLocked: isLocked(swSettings.components.WebMarketAnalysisOverviewHome),
            isHidden: isHidden(swSettings.components.WebMarketAnalysisOverviewHome),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.IndustryAnalysis,
            onClickLocked: onClickLockedCallback,
        },
        {
            title: "primary.sidebar.website.segments.title",
            icon: "sidebar3-website-segments",
            state: EPrimarySideBar3StateType.Segments_homepage,
            ParentTrackName: "Competitive Research",
            id: EPrimarySideBar3IdType.WebsiteSegmentsAnalysis,
            isHidden: isHidden(swSettings.components.CustomSegments),
            isLocked: isLocked(swSettings.components.CustomSegments),
            onClickLocked: () => onClickLockedCallback("websiteSegments", "v5"),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.SegmentAnalysis,
        },
    ]);
};

const MarketAnalysisSubSection = (): ISubSectionItem[] => {
    const onClickLockedCallback = (key: string, version?: string) =>
        onHookClick(key, "digitalsuite_webmarketanalysis_home", version);

    return filterOutLockedItems([
        {
            title: "primary.sidebar.market.analysis.title",
            icon: "sidebar3-market-analysis",
            state: EPrimarySideBar3StateType.WebMarketResearch_home,
            ParentTrackName: "Market Analysis",
            id: EPrimarySideBar3IdType.WebMarketResearch,
            isLocked: isLocked(swSettings.components.WebMarketResearchOverviewHome),
            onClickLocked: () => onClickLockedCallback("MarketAnalysis", "v5"),
            isHidden: isHidden(swSettings.components.WebMarketResearchOverviewHome),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
        },
        {
            title: "primary.sidebar.demand.analysis.title",
            icon: "sidebar3-search-interests",
            state: EPrimarySideBar3StateType.MarketResearch_keywordMarketResearch_home,
            ParentTrackName: "Market Analysis",
            id: EPrimarySideBar3IdType.DemandAnalysis,
            isLocked: isLocked(swSettings.components.TopicMarketAnalysisHome),
            onClickLocked: () => onClickLockedCallback("DemandAnalysis", "v5"),
            isHidden: isHidden(swSettings.components.TopicMarketAnalysisHome),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.SearchInterest,
        },
        { ...buyerJourney, itemType: EPrimaryItemType.GroupItem },
    ]);
};

const genAIIntelligenceSubSection = (): ISubSectionItem[] => {
    return filterOutLockedItems([
        {
            title: "primary.sidebar.llm.tracking.title",
            icon: "sidebar3-ai-brand-visibility",
            state: "digitalsuite_llm_solutions_homepage",
            ParentTrackName: "LLM Tracking",
            id: EPrimarySideBar3IdType.LLMTracker,
            secondaryBarType3: SecondarySideBarType3.LLMTracking,
            itemType: EPrimaryItemType.GroupItem,
            onClick: handleAIBrandVisibilityClick,
            isHidden: isHidden(swSettings.components.AiTracker),
        },
        {
            title: "primary.sidebar.ai.research.title",
            icon: "ai-topic",
            state: AIResearchRoutes.home,
            ParentTrackName: "AI Research",
            id: EPrimarySideBar3IdType.AIResearch,
            secondaryBarType3: SecondarySideBarType3.AIResearch,
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            badgeData: { badgeText: BADGE_ITEMS.BETA, badgeType: BADGE_ITEMS.BETA },
            isLocked: isLocked(swSettings.components.AIResearch),
            isHidden: isHidden(swSettings.components.AIResearch),
            onClickLocked: () => openHooksModalV5("AIResearch"),
        },
        {
            title: "analysis.ai.referral.title",
            icon: "sidebar3-llm",
            state: AITrafficRoutes.overview,
            ParentTrackName: "LLM Tracking",
            id: EPrimarySideBar3IdType.LLMTraffic,
            secondaryBarType3: SecondarySideBarType3.LLMTraffic,
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            isHidden: isHidden(swSettings.components.AIRefferrals),
            isLocked: isLocked(swSettings.components.AIRefferrals),
            upsellBadgeComponent: isLocked(swSettings.components.AIRefferrals) ? getUnlockBadge() : undefined,
            onClickLocked: () =>
                onHookClick("chatbotAITraffic", EPrimarySideBar3StateType.LLMTraffic, getHooksVersion(swSettings)),
        },
        {
            title: "llm.reports.tab.content_opportunities.title",
            icon: Airecommendation,
            state: recommendationsRoutes.overview,
            ParentTrackName: "Content Opportunities",
            id: EPrimarySideBar3IdType.Recommendations,
            secondaryBarType3: SecondarySideBarType3.Recommendations,
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            onClickLocked: () =>
                onHookClick(
                    "Recommendations",
                    EPrimarySideBar3StateType.Recommendations_overview,
                    getHooksVersion(swSettings),
                ),
            isHidden: isHidden(swSettings.components.GenaiRecommendations),
            isLocked: isLocked(swSettings.components.GenaiRecommendations),
            badgeData: { badgeText: BADGE_ITEMS.BETA, badgeType: BADGE_ITEMS.BETA },
        },
    ]);
};

const SearchIntelligenceSubSection = (): ISubSectionItem[] => {
    return filterOutLockedItems([
        {
            title: "primary.sidebar.search.tracking.title",
            icon: "sidebar3-rank-tracker",
            state: "ranktracker.home",
            ParentTrackName: "Campaigns",
            id: EPrimarySideBar3IdType.SearchIntelligence,
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.SearchTracking,
            onClickLocked: () => {
                onHookClick(
                    "RankTracker_Sidebar",
                    EPrimarySideBar3StateType.RankTracker_home,
                    getHooksVersion(swSettings),
                );
            },
            isHidden: isHidden(swSettings.components.RankTracker),
            isLocked: isLocked(swSettings.components.RankTracker),
            upsellBadgeComponent: isLocked(swSettings.components.RankTracker) ? getUnlockBadge() : undefined,
        },
        {
            title: "primary.sidebar.keyword.research.title",
            icon: "sidebar3-seo",
            state: "organicsearch_websiteanalysis_home",
            ParentTrackName: "Keyword Research",
            id: EPrimarySideBar3IdType.KeywordResearch,
            isLocked: isLocked(swSettings.components.DigitalSuiteAcquisition),
            isHidden: isHidden(swSettings.components.DigitalSuiteAcquisition),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            upsellBadgeComponent: isLocked(swSettings.components.DigitalSuiteAcquisition)
                ? getUnlockBadge()
                : undefined,
            secondaryBarType3: SecondarySideBarType3.KeywordResearch,
            onClickLocked: () => {
                onHookClick("keywordResearch", EPrimarySideBar3StateType.KeywordResearch, getHooksVersion(swSettings));
            },
        },
        {
            title: "primary.sidebar.backlinks.analysis.title",
            icon: "organic-link",
            state: "digitalsuite_backlinks_overview",
            ParentTrackName: "Backlinks Analysis",
            id: EPrimarySideBar3IdType.BacklinksAnalysis,
            isLocked: isLocked(swSettings.components.Backlinks),
            isHidden: isHidden(swSettings.components.Backlinks),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.BacklinksAnalysis,
            onClickLocked: () =>
                onHookClick("Backlinks", EPrimarySideBar3StateType.Backlinks_home, getHooksVersion(swSettings)),
            upsellBadgeComponent: isLocked(swSettings.components.Backlinks) ? getUnlockBadge() : undefined,
        },
        {
            title: "primary.sidebar.site.audit.title",
            icon: "site-audit-v2",
            state: "digitalsuite_site_audit_home",
            ParentTrackName: "Site Audit",
            id: EPrimarySideBar3IdType.SiteAudit,
            isLocked: isLocked(swSettings.components.SiteAuditFeature),
            isHidden: isHidden(swSettings.components.SiteAuditFeature),
            onClick: onSubSectionClick,
            itemType: EPrimaryItemType.GroupItem,
            secondaryBarType3: SecondarySideBarType3.SiteAudit,
            onClickLocked: () =>
                onHookClick("siteAudit", EPrimarySideBar3StateType.SiteAudit_home, getHooksVersion(swSettings)),
            upsellBadgeComponent: isLocked(swSettings.components.SiteAuditFeature) ? getUnlockBadge() : undefined,
        },
    ]);
};

const AdIntelligenceSubSection = (): ISubSectionItem[] =>
    filterOutLockedItems([
        {
            title: "primary.sidebar.advertiser.activity.title",
            icon: "ad-research",
            state: advertiserActivityRoutes.home,
            ParentTrackName: "Advertiser Activity",
            id: EPrimarySideBar3IdType.AdvertiserActivity,
            isLocked: isAdIntelligenceNavigationLocked() && !adSearchIsOpen(),
            isHidden: isHidden(swSettings.components.AdIntMaster) && !adSearchIsOpen(),
            upsellBadgeComponent:
                isAdIntelligenceNavigationLocked() && !adSearchIsOpen() ? getUnlockBadge() : undefined,
            onClickLocked: () => openAdIntelligenceHookModal(),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.AdvertiserActivity,
        },
        {
            title: "primary.sidebar.publisher.analysis.title",
            icon: "publish",
            state: publisherAnalysisRoutes.home,
            ParentTrackName: "Publisher Analysis",
            id: EPrimarySideBar3IdType.PublisherAnalysis,
            isLocked: isAdIntelligenceNavigationLocked(),
            isHidden: isHidden(swSettings.components.AdIntMaster),
            upsellBadgeComponent: isAdIntelligenceNavigationLocked() ? getUnlockBadge() : undefined,
            onClickLocked: () => openAdIntelligenceHookModal("publisher"),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.PublisherAnalysis,
        },
        {
            title: "primary.sidebar.monitor.protect.title",
            icon: "sidebar3-brand-protection",
            state: "monitorandprotect.home",
            ParentTrackName: "MonitorAndProtect",
            id: EPrimarySideBar3IdType.MonitorAndProtect,
            isLocked: false, // should not be locked
            isHidden: !isAvailable(swSettings.components.MonitorAndProtectMaster),
            itemType: EPrimaryItemType.GroupItem,
            onClick: onSubSectionClick,
            secondaryBarType3: SecondarySideBarType3.MonitorAndProtect,
        },
    ]);

export const competitiveResearch = {
    title: "primary.sidebar.websites.research.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    groupedSubSection: CompetitiveResearchSubSection(),
    placement: ESidebarPlacement.Top,
};

export const searchIntelligence = {
    title: "primary.sidebar.search.intelligence.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    groupedSubSection: SearchIntelligenceSubSection(),
    placement: ESidebarPlacement.Top,
};
export const genAIIntelligence = {
    title: "primary.sidebar.genAI.intelligence.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    groupedSubSection: genAIIntelligenceSubSection(),
    placement: ESidebarPlacement.Top,
};

export const marketResearch = {
    title: "primary.sidebar.market.research.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    groupedSubSection: MarketAnalysisSubSection(),
    placement: ESidebarPlacement.Top,
};

export const adIntelligence = {
    title: "primary.sidebar.ad.intelligence.group.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    groupedSubSection: AdIntelligenceSubSection(),
    placement: ESidebarPlacement.Top,
};
